/**
 * SC-209 phase 3: whether the floating window is allowed to open on its own.
 *
 * One preference, stored the way the room's other per-person preferences are stored — a bare `localStorage`
 * string, read on demand rather than held in Redux. `preferAudioEnabled` and `preferVideoEnabled` work the
 * same way and for the same reason: they have to be readable from anywhere, including from a handler that
 * cannot afford to wait for a render.
 *
 * **Absent means on.** Automatic opening is the feature — a window you have to remember to open is a window
 * you will not have open when you need it — and the switch exists for the person who finds it intrusive, not
 * as a second thing to discover before the feature works at all. It is per-person and per-browser on purpose:
 * this is a preference about a window on somebody's screen, not a policy an office sets.
 */

export type PipAutoOpen = 'on' | 'never'

export const PIP_AUTO_OPEN_KEY = 'preferPipAutoOpen'

/** Anything that is not the literal `'never'` reads as on, so a corrupted value fails towards the feature. */
export function readPipAutoOpen(): PipAutoOpen {
  try {
    return localStorage.getItem(PIP_AUTO_OPEN_KEY) === 'never' ? 'never' : 'on'
  } catch {
    // Private mode, or storage the browser has decided to refuse. A refusal to remember is not a refusal.
    return 'on'
  }
}

export function writePipAutoOpen(value: PipAutoOpen): void {
  try {
    localStorage.setItem(PIP_AUTO_OPEN_KEY, value)
  } catch {
    // Nothing useful to do and nothing worth telling the user: the setting simply will not survive a reload.
  }
}

/** True when nothing should open by itself — the one question every automatic caller actually asks. */
export function pipAutoOpenBlocked(): boolean {
  return readPipAutoOpen() === 'never'
}
